import { useEffect, useState, useCallback } from "react";
import { X, Plus, Trash2, Pencil, Check, Loader2, Star, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", rfc: "", regimen: "601 - General de Ley Personas Morales", is_default: false };

// Gestión de empresas fiscales (Contabilidad/Finanzas) por empresa/tenant. Solo super-admin.
export default function FiscalCompaniesDialog({ open, tenant, onClose }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!tenant) return;
    setLoading(true);
    api.get(`/tenants/${tenant.id}/fiscal-companies`)
      .then((r) => setCompanies(r.data))
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
  }, [tenant]);

  useEffect(() => { if (open) { load(); setEditId(null); setForm(EMPTY); } }, [open, load]);

  if (!open || !tenant) return null;

  const startEdit = (c) => { setEditId(c.id); setForm({ name: c.name, rfc: c.rfc || "", regimen: c.regimen || EMPTY.regimen, is_default: !!c.is_default }); };
  const startNew = () => { setEditId("new"); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editId === "new") await api.post(`/tenants/${tenant.id}/fiscal-companies`, form);
      else await api.put(`/tenants/${tenant.id}/fiscal-companies/${editId}`, form);
      toast.success("Empresa fiscal guardada");
      setEditId(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar la empresa fiscal "${c.name}"?`)) return;
    try { await api.delete(`/tenants/${tenant.id}/fiscal-companies/${c.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" data-testid="fiscal-companies-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-violet" />
            <div>
              <h3 className="text-base font-medium">Empresas fiscales</h3>
              <p className="text-xs text-navy/40">{tenant.name} · Finanzas / Contabilidad</p>
            </div>
          </div>
          <button data-testid="fiscal-dialog-close" onClick={onClose} className="p-1.5 rounded-lg text-navy/40 hover:text-navy hover:bg-navy/5"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <>
              {companies.length === 0 && <p className="text-sm text-navy/40 py-2">Aún no hay empresas fiscales. Agrega la primera.</p>}
              {companies.map((c) => (
                editId === c.id ? (
                  <CompanyForm key={c.id} form={form} setForm={setForm} save={save} cancel={() => setEditId(null)} saving={saving} />
                ) : (
                  <div key={c.id} data-testid={`fiscal-company-${c.id}`} className="flex items-center gap-3 border border-navy/10 rounded-lg px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium flex items-center gap-2 truncate">
                        {c.name}
                        {c.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet/10 text-violet inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Predeterminada</span>}
                      </div>
                      <div className="text-xs text-navy/45 font-mono">{c.rfc || "RFC pendiente"} · {c.regimen}</div>
                    </div>
                    <button data-testid={`fiscal-edit-${c.id}`} onClick={() => startEdit(c)} className="p-2 rounded-lg text-navy/30 hover:text-violet hover:bg-violet/5"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`fiscal-del-${c.id}`} onClick={() => del(c)} className="p-2 rounded-lg text-navy/30 hover:text-destructive hover:bg-destructive/5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )
              ))}

              {editId === "new" ? (
                <CompanyForm form={form} setForm={setForm} save={save} cancel={() => setEditId(null)} saving={saving} isNew />
              ) : (
                <button data-testid="fiscal-add-btn" onClick={startNew} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-gradient-violet text-white px-4 py-2 hover:opacity-90 transition-opacity">
                  <Plus className="w-4 h-4" /> Agregar empresa fiscal
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyForm({ form, setForm, save, cancel, saving, isNew }) {
  const input = "w-full border border-navy/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet";
  return (
    <div className="border border-violet/30 rounded-lg p-4 space-y-3 bg-violet/[0.02]" data-testid="fiscal-company-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-navy/50 mb-1 block">Nombre / Razón social</label>
          <input data-testid="fiscal-form-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="King of Kongo" />
        </div>
        <div>
          <label className="text-xs text-navy/50 mb-1 block">RFC</label>
          <input data-testid="fiscal-form-rfc" className={`${input} font-mono uppercase`} value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} placeholder="XAXX010101000" />
        </div>
      </div>
      <div>
        <label className="text-xs text-navy/50 mb-1 block">Régimen fiscal</label>
        <input data-testid="fiscal-form-regimen" className={input} value={form.regimen} onChange={(e) => setForm({ ...form, regimen: e.target.value })} />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input data-testid="fiscal-form-default" type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-violet w-4 h-4" />
        Empresa predeterminada
      </label>
      <div className="flex items-center gap-2 pt-1">
        <button data-testid="fiscal-form-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-gradient-violet text-white px-4 py-2 hover:opacity-90 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {isNew ? "Crear" : "Guardar"}
        </button>
        <button data-testid="fiscal-form-cancel" onClick={cancel} className="text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-navy/30">Cancelar</button>
      </div>
    </div>
  );
}
